import React, { useState, useEffect, useCallback } from 'react';
import ApiClient, { Submission, SubmissionStatus, SubmissionListParams, Form, SubmissionsResponse, IPSubmissionsData, ASNSubmissionsData } from '../api/client';
import SubmissionDetail, { SUBMISSION_STATUS_TITLES } from './SubmissionDetail';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { usePermissions } from '../context/PermissionContext';
import { DataTableShell, PageHeader, SortableTableHeader } from './ui';
import { AlertIcon, BanIcon, CheckIcon, CloseIcon, DownloadIcon, FlagIcon, InboxIcon, RefreshIcon, TrashIcon } from './icons';

interface SubmissionManagerProps {
  apiClient: ApiClient;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// URL param helpers
const getUrlParams = () => new URLSearchParams(window.location.search);

const updateUrlParams = (params: Record<string, string | number | undefined>) => {
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== 0) {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.replaceState({}, '', url.toString());
};

// Status pill classes (shared palette in App.css).
const STATUS_CLASS: Record<SubmissionStatus, string> = {
  pending: 'status-pending',
  approved: 'status-approved',
  flagged: 'status-flagged',
  spam: 'status-spam',
  blacklisted: 'status-submission-blacklisted',
  shadowbanned: 'status-shadowbanned',
};

const STATUS_FILTER_OPTIONS: { value: SubmissionStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'spam', label: 'Spam' },
  { value: 'blacklisted', label: 'Blacklisted' },
  { value: 'shadowbanned', label: 'Shadowbanned' },
];

// Per-browser preference for including shadowbanned submissions in the feed.
export const SHOW_SHADOWBANNED_STORAGE_KEY = 'fp.submissions.showShadowbanned';

const readShowShadowbanned = (): boolean => {
  try {
    return window.localStorage.getItem(SHOW_SHADOWBANNED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeShowShadowbanned = (value: boolean) => {
  try {
    if (value) {
      window.localStorage.setItem(SHOW_SHADOWBANNED_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(SHOW_SHADOWBANNED_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode, blocked site data); keep in-memory state only.
  }
};

const SHADOWBANNED_APPROVE_MESSAGE =
  "This submission was shadowbanned by security rules. Approving it will deliver the lead to the form's notification channels.";

type SortField = 'id' | 'status' | 'spamScore' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const buildListParams = (options: {
  pageOffset: number;
  pageSize: number;
  sortField: SortField;
  sortDirection: SortDirection;
  statusFilter: SubmissionStatus | '';
  formFilter: string;
  showShadowbanned: boolean;
}): SubmissionListParams => {
  const params: SubmissionListParams = {
    limit: options.pageSize,
    page: Math.floor(options.pageOffset / options.pageSize) + 1,
    sortBy: options.sortField,
    sortDirection: options.sortDirection,
  };
  if (options.statusFilter) params.status = options.statusFilter;
  if (options.formFilter) params.formHash = options.formFilter;
  if (options.showShadowbanned) params.includeShadowbanned = true;
  return params;
};

const SubmissionManager: React.FC<SubmissionManagerProps> = ({ apiClient }) => {
  const { hasPermission } = usePermissions();

  // Initialize state from URL params
  const initialParams = getUrlParams();

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Permissions
  const canModerate = hasPermission('submissions.moderate');
  const canDelete = hasPermission('submissions.delete');

  // Filters (from URL)
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | ''>(
    (initialParams.get('status') as SubmissionStatus) || ''
  );
  const [formFilter, setFormFilter] = useState<string>(
    initialParams.get('form') || ''
  );
  const [showShadowbanned, setShowShadowbanned] = useState<boolean>(readShowShadowbanned);

  // IP/ASN drill-down filters
  const [ipFilter, setIpFilter] = useState<number | null>(
    initialParams.get('ipId') ? Number(initialParams.get('ipId')) : null
  );
  const [asnFilter, setAsnFilter] = useState<number | null>(
    initialParams.get('asn') ? Number(initialParams.get('asn')) : null
  );
  const [ipInfo, setIpInfo] = useState<IPSubmissionsData['ip'] | null>(null);
  const [asnInfo, setAsnInfo] = useState<ASNSubmissionsData['asn'] | null>(null);

  // Sorting (from URL)
  const [sortField, setSortField] = useState<SortField>(
    (initialParams.get('sortBy') as SortField) || 'createdAt'
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (initialParams.get('sortDir') as SortDirection) || 'desc'
  );

  // Pagination (from URL)
  const [pageSize, setPageSize] = useState<number>(
    Number(initialParams.get('limit')) || DEFAULT_PAGE_SIZE
  );
  const [offset, setOffset] = useState(
    Number(initialParams.get('offset')) || 0
  );
  const [total, setTotal] = useState(0);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<'approve' | 'flag' | 'spam' | 'delete' | null>(null);

  // Shadowbanned approval confirmation (approving delivers the lead to channels)
  const [approveConfirmId, setApproveConfirmId] = useState<number | null>(null);

  // Export
  const [exporting, setExporting] = useState(false);

  // Pending count
  const [pendingCount, setPendingCount] = useState(0);

  const loadSubmissions = useCallback(async (reset = false) => {
    setLoading(true);
    setError(null);
    try {
      const newOffset = reset ? 0 : offset;

      // Use drill-down endpoints if IP or ASN filter is set
      if (ipFilter) {
        const data = await apiClient.getIPSubmissions(ipFilter, pageSize);
        setIpInfo(data.ip);
        // Map IP submissions to standard Submission format
        const subs = Array.isArray(data.submissions) ? data.submissions : [];
        const mapped = subs.map(s => ({
          ...s,
          submittingIp: data.ip.ipAddress,
        })) as unknown as Submission[];
        setSubmissions(mapped);
        setTotal(subs.length);
        if (reset) {
          setOffset(0);
          setSelectedIds(new Set());
        }
        return;
      }

      if (asnFilter) {
        const data = await apiClient.getASNSubmissions(asnFilter, pageSize);
        setAsnInfo(data.asn);
        // Map ASN submissions to standard Submission format
        const subs = Array.isArray(data.submissions) ? data.submissions : [];
        const mapped = subs as unknown as Submission[];
        setSubmissions(mapped);
        setTotal(subs.length);
        if (reset) {
          setOffset(0);
          setSelectedIds(new Set());
        }
        return;
      }

      // Standard submissions list
      const params = buildListParams({
        pageOffset: newOffset,
        pageSize,
        sortField,
        sortDirection,
        statusFilter,
        formFilter,
        showShadowbanned,
      });

      const response = await apiClient.listSubmissions(params);

      setSubmissions(response.submissions);
      setTotal(response.meta.total || 0);
      setIpInfo(null);
      setAsnInfo(null);
      if (reset) {
        setOffset(0);
        setSelectedIds(new Set());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [apiClient, offset, pageSize, statusFilter, formFilter, showShadowbanned, sortField, sortDirection, ipFilter, asnFilter]);

  const loadForms = useCallback(async () => {
    try {
      const data = await apiClient.listForms();
      setForms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load forms for filter:', err);
    }
  }, [apiClient]);

  const loadPendingCount = useCallback(async () => {
    try {
      const response = await apiClient.listSubmissions({ status: 'pending', limit: 1 });
      setPendingCount(response.meta.total || 0);
    } catch (err) {
      console.error('Failed to load pending count:', err);
    }
  }, [apiClient]);

  useEffect(() => {
    loadForms();
    loadPendingCount();
  }, [loadForms, loadPendingCount]);

  // Sync state to URL params
  useEffect(() => {
    updateUrlParams({
      status: statusFilter || undefined,
      form: formFilter || undefined,
      sortBy: sortField !== 'createdAt' ? sortField : undefined,
      sortDir: sortDirection !== 'desc' ? sortDirection : undefined,
      limit: pageSize !== DEFAULT_PAGE_SIZE ? pageSize : undefined,
      offset: offset || undefined,
      ipId: ipFilter || undefined,
      asn: asnFilter || undefined,
    });
  }, [statusFilter, formFilter, sortField, sortDirection, pageSize, offset, ipFilter, asnFilter]);

  useEffect(() => {
    writeShowShadowbanned(showShadowbanned);
  }, [showShadowbanned]);

  // Reload when filters or sort changes (reset to page 1)
  useEffect(() => {
    loadSubmissions(true);
  }, [statusFilter, formFilter, showShadowbanned, sortField, sortDirection, pageSize, ipFilter, asnFilter]);

  // Reload when offset changes (pagination)
  useEffect(() => {
    if (offset > 0) {
      loadSubmissions(false);
    }
  }, [offset]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortHeader: React.FC<{ field: SortField; children: React.ReactNode; align?: 'right' }> = ({ field, children, align }) => (
    <SortableTableHeader
      active={sortField === field}
      direction={sortDirection}
      onClick={() => handleSort(field)}
      align={align}
      className={align === 'right' ? 'numeric-cell' : undefined}
    >
      {children}
    </SortableTableHeader>
  );

  const handleStatusChange = async (id: number, action: 'approve' | 'flag' | 'spam') => {
    try {
      let updated: Submission;
      switch (action) {
        case 'approve': {
          const approved = await apiClient.approveSubmission(id);
          updated = approved;
          if (approved.knownBadEmailsReleased) {
            setSuccess(`Approved submission #${id}; released ${approved.knownBadEmailsReleased} known-bad email(s)`);
          }
          break;
        }
        case 'flag':
          updated = await apiClient.flagSubmission(id);
          break;
        case 'spam':
          updated = await apiClient.markSubmissionSpam(id);
          break;
      }
      setSubmissions(submissions.map(s => s.id === id ? { ...s, ...updated } : s));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, ...updated });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update submission');
    }
  };

  // Approving a shadowbanned submission delivers it, so ask first.
  const requestStatusChange = (submission: Submission, action: 'approve' | 'flag' | 'spam') => {
    if (action === 'approve' && submission.status === 'shadowbanned') {
      setApproveConfirmId(submission.id);
      return;
    }
    handleStatusChange(submission.id, action);
  };

  const handleConfirmApprove = async () => {
    if (approveConfirmId === null) return;
    const id = approveConfirmId;
    setApproveConfirmId(null);
    await handleStatusChange(id, 'approve');
  };

  const selectedShadowbannedCount = submissions.filter(
    s => selectedIds.has(s.id) && s.status === 'shadowbanned'
  ).length;

  const handleDelete = async () => {
    if (deletingId === null) return;
    try {
      await apiClient.deleteSubmission(deletingId);
      setSubmissions(submissions.filter(s => s.id !== deletingId));
      if (selectedSubmission?.id === deletingId) {
        setSelectedSubmission(null);
      }
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(deletingId);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete submission');
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewDetail = async (submission: Submission) => {
    try {
      const detail = await apiClient.getSubmission(submission.id);
      setSelectedSubmission(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submission details');
    }
  };

  // Bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    try {
      switch (bulkAction) {
        case 'approve': {
          const result = await apiClient.bulkApproveSubmissions(ids);
          const released = result.knownBadEmailsReleased
            ? `; released ${result.knownBadEmailsReleased} known-bad email(s)`
            : '';
          setSuccess(`Approved ${result.approved} submissions${released}`);
          break;
        }
        case 'flag': {
          const result = await apiClient.bulkFlagSubmissions(ids);
          setSuccess(`Flagged ${result.flagged} submissions`);
          break;
        }
        case 'spam': {
          const result = await apiClient.bulkMarkSubmissionsSpam(ids);
          setSuccess(`Marked ${result.markedSpam} submissions as spam`);
          break;
        }
        case 'delete': {
          const result = await apiClient.bulkDeleteSubmissions(ids);
          setSuccess(`Deleted ${result.deleted} submissions`);
          break;
        }
      }
      setSelectedIds(new Set());
      setBulkAction(null);
      await loadSubmissions(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === submissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map(s => s.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Export
  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true);
    try {
      const blob = await apiClient.exportSubmissions({
        format,
        formHash: formFilter || undefined,
        status: statusFilter || undefined,
        includeShadowbanned: showShadowbanned || undefined,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submissions-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccess(`Exported submissions as ${format.toUpperCase()}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export submissions');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getFormName = (hash: string) => {
    const form = forms.find(f => f.hash === hash);
    return form?.name || hash.substring(0, 8) + '...';
  };

  return (
    <div className="manager-page submissions-page">
      {error && (
        <div className="alert alert-error">
          <AlertIcon size={15} />
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✓</span>
          <span>{success}</span>
          <button className="alert-close" onClick={() => setSuccess(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      <PageHeader
        title="Submissions"
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('csv')}
              disabled={exporting}
              title="Export as CSV"
            >
              <DownloadIcon size={14} />
              CSV
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('json')}
              disabled={exporting}
              title="Export as JSON"
            >
              <DownloadIcon size={14} />
              JSON
            </button>
            <button className="btn btn-primary" onClick={() => { loadSubmissions(true); loadPendingCount(); }}>
              <RefreshIcon size={14} />
              Refresh
            </button>
          </>
        }
      />

      {pendingCount > 0 && (
        <div className="submissions-status-line">
          {pendingCount > 0 && (
            <span className="status-pill status-pill-processing">
              <span />
              {pendingCount} processing
            </span>
          )}
        </div>
      )}

      <div className="submissions-shell">
        {/* IP/ASN Filter Banner */}
        {(ipInfo || asnInfo) && (
          <div className="filter-banner">
            <div>
              {ipInfo && (
                <span>
                  Showing submissions from IP <strong className="mono">{ipInfo.ipAddress}</strong>
                  {ipInfo.listType !== 'neutral' && (
                    <span className={`status-badge ${ipInfo.listType === 'whitelist' ? 'status-whitelisted' : 'status-blacklisted'}`}>
                      {ipInfo.listType}
                    </span>
                  )}
                </span>
              )}
              {asnInfo && (
                <span>
                  Showing submissions from ASN <strong>AS{asnInfo.asn}</strong>
                  {asnInfo.name && <span> ({asnInfo.name})</span>}
                  {asnInfo.shadowbanned && (
                    <span className="status-badge status-banned">
                      BANNED
                    </span>
                  )}
                </span>
              )}
            </div>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => {
                setIpFilter(null);
                setAsnFilter(null);
                setIpInfo(null);
                setAsnInfo(null);
              }}
            >
              <CloseIcon size={14} />
              Clear Filter
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="submissions-filters">
          <div className="filter-field">
            <label htmlFor="submissions-status-filter">Status</label>
            <select
              id="submissions-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SubmissionStatus | '')}
            >
              <option value="">All Statuses</option>
              {STATUS_FILTER_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="submissions-form-filter">Form</label>
            <select
              id="submissions-form-filter"
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
            >
              <option value="">All Forms</option>
              {(Array.isArray(forms) ? forms : []).map(form => (
                <option key={form.hash} value={form.hash}>{form.name}</option>
              ))}
            </select>
          </div>
          <label
            className="submissions-filter-toggle"
            title={statusFilter
              ? 'The status filter overrides this setting'
              : 'Shadowbanned submissions are hidden by default'}
          >
            <input
              type="checkbox"
              checked={showShadowbanned}
              onChange={(e) => setShowShadowbanned(e.target.checked)}
            />
            Show shadowbanned
          </label>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (canModerate || canDelete) && (
          <div className="bulk-bar">
            <span className="bulk-bar-count">{selectedIds.size} selected</span>
            <div className="bulk-bar-actions">
              {canModerate && (
                <>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => setBulkAction('approve')}
                  >
                    ✓ Approve
                  </button>
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => setBulkAction('flag')}
                  >
                    ⚑ Flag
                  </button>
                  <button
                    className="btn btn-sm btn-danger-soft"
                    onClick={() => setBulkAction('spam')}
                  >
                    ✕ Spam
                  </button>
                </>
              )}
              {canDelete && (
                <button
                  className="btn btn-sm btn-danger-soft"
                  onClick={() => setBulkAction('delete')}
                >
                  <TrashIcon size={14} />
                  Delete
                </button>
              )}
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Submissions Table - Scrollable */}
        {loading && submissions.length === 0 ? (
          <div className="empty-state submissions-empty">
            <div className="loading-spinner"></div>
            <p>Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="empty-state submissions-empty">
            <div className="empty-state-icon"><InboxIcon size={28} /></div>
            <h3>No submissions found</h3>
            <p>No submissions match the current filters</p>
          </div>
        ) : (
          <DataTableShell>
            <table className="data-table">
              <thead>
                <tr>
                  <th className="checkbox-cell">
                    <input
                      type="checkbox"
                      aria-label="Select all submissions"
                      checked={selectedIds.size === submissions.length && submissions.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <SortHeader field="id">ID</SortHeader>
                  <th>Form</th>
                  <SortHeader field="status">Status</SortHeader>
                  <SortHeader field="spamScore" align="right">Spam Score</SortHeader>
                  <th>Origin</th>
                  <SortHeader field="createdAt">Date</SortHeader>
                  <th className="actions-cell"><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(submission => (
                  <tr
                    key={submission.id}
                    className={`is-clickable ${selectedIds.has(submission.id) ? 'is-selected' : ''}`}
                    onClick={() => handleViewDetail(submission)}
                  >
                    <td className="checkbox-cell" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select submission ${submission.id}`}
                        checked={selectedIds.has(submission.id)}
                        onChange={() => toggleSelect(submission.id)}
                      />
                    </td>
                    <td className="data-id">#{submission.id}</td>
                    <td className="data-title">{getFormName(submission.formHash)}</td>
                    <td>
                      <span
                        title={SUBMISSION_STATUS_TITLES[submission.status]}
                        className={`status-badge ${STATUS_CLASS[submission.status] || 'status-badge-neutral'}`}
                      >
                        {submission.status}
                      </span>
                      {submission.testMode && (
                        <span className="status-badge status-test submission-test-badge">
                          TEST
                        </span>
                      )}
                    </td>
                    <td className="numeric-cell">
                      <span className={`spam-score ${submission.spamScore > 0.7 ? 'high' : submission.spamScore > 0.4 ? 'medium' : 'low'}`}>
                        {(submission.spamScore * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="data-muted">
                      {submission.origin || '-'}
                    </td>
                    <td className="data-muted">
                      {formatDate(submission.createdAt)}
                    </td>
                    <td className="actions-cell" onClick={e => e.stopPropagation()}>
                      <div className="row-actions">
                        {canModerate && (
                          <>
                            {submission.status !== 'approved' && (
                              <button
                                className="icon-btn intent-success"
                                onClick={() => requestStatusChange(submission, 'approve')}
                                title="Approve"
                                aria-label="Approve"
                              >
                                <CheckIcon />
                              </button>
                            )}
                            {submission.status !== 'flagged' && (
                              <button
                                className="icon-btn intent-warning"
                                onClick={() => handleStatusChange(submission.id, 'flag')}
                                title="Flag"
                                aria-label="Flag"
                              >
                                <FlagIcon />
                              </button>
                            )}
                            {submission.status !== 'spam' && (
                              <button
                                className="icon-btn intent-danger"
                                onClick={() => handleStatusChange(submission.id, 'spam')}
                                title="Mark as Spam"
                                aria-label="Mark as Spam"
                              >
                                <BanIcon />
                              </button>
                            )}
                          </>
                        )}
                        {canDelete && (
                          <button
                            className="icon-btn intent-danger"
                            onClick={() => setDeletingId(submission.id)}
                            title="Delete"
                            aria-label="Delete"
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableShell>
        )}

        {/* Pagination - Always at bottom */}
        {total > 0 && (
          <div className="table-pagination">
            <button
              className="btn btn-secondary"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
            >
              ← Previous
            </button>
            <div className="table-pagination-info">
              <span>
                Showing {offset + 1}-{Math.min(offset + pageSize, total)} of {total.toLocaleString()}
              </span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-secondary"
              disabled={offset + pageSize >= total || loading}
              onClick={() => setOffset(offset + pageSize)}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (() => {
        const currentIndex = submissions.findIndex(s => s.id === selectedSubmission.id);
        const globalIndex = offset + currentIndex;
        const hasPrev = globalIndex > 0;
        const hasNext = globalIndex < total - 1;

        const handlePrev = async () => {
          if (currentIndex > 0) {
            // Move within current page - fetch full detail
            const detail = await apiClient.getSubmission(submissions[currentIndex - 1].id);
            setSelectedSubmission(detail);
          } else if (offset > 0) {
            // Fetch previous page and select last item
            const newOffset = Math.max(0, offset - pageSize);
            const params = buildListParams({
              pageOffset: newOffset,
              pageSize,
              sortField,
              sortDirection,
              statusFilter,
              formFilter,
              showShadowbanned,
            });

            const response = await apiClient.listSubmissions(params);
            setSubmissions(response.submissions);
            setOffset(newOffset);
            // Fetch full detail for the last item
            const detail = await apiClient.getSubmission(response.submissions[response.submissions.length - 1].id);
            setSelectedSubmission(detail);
          }
        };

        const handleNext = async () => {
          if (currentIndex < submissions.length - 1) {
            // Move within current page - fetch full detail
            const detail = await apiClient.getSubmission(submissions[currentIndex + 1].id);
            setSelectedSubmission(detail);
          } else if (offset + pageSize < total) {
            // Fetch next page and select first item
            const newOffset = offset + pageSize;
            const params = buildListParams({
              pageOffset: newOffset,
              pageSize,
              sortField,
              sortDirection,
              statusFilter,
              formFilter,
              showShadowbanned,
            });

            const response = await apiClient.listSubmissions(params);
            setSubmissions(response.submissions);
            setOffset(newOffset);
            // Fetch full detail for the first item
            const detail = await apiClient.getSubmission(response.submissions[0].id);
            setSelectedSubmission(detail);
          }
        };

        return (
          <SubmissionDetail
            submission={selectedSubmission}
            formName={getFormName(selectedSubmission.formHash)}
            onClose={() => setSelectedSubmission(null)}
            onStatusChange={canModerate ? (action: 'approve' | 'flag' | 'spam') => requestStatusChange(selectedSubmission, action) : undefined}
            onDelete={canDelete ? () => setDeletingId(selectedSubmission.id) : undefined}
            onPrev={handlePrev}
            onNext={handleNext}
            hasPrev={hasPrev}
            hasNext={hasNext}
          />
        );
      })()}

      {/* Delete Confirmation */}
      {deletingId !== null && (
        <ConfirmDeleteModal
          title="Delete Submission"
          message="Delete this submission? This action cannot be undone."
          confirmButtonLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeletingId(null)}
        />
      )}

      {/* Shadowbanned Approve Confirmation */}
      {approveConfirmId !== null && (
        <ConfirmDeleteModal
          title="Approve Shadowbanned Submission"
          message={`${SHADOWBANNED_APPROVE_MESSAGE} Approve submission #${approveConfirmId}?`}
          confirmButtonLabel="✓ Approve and Deliver"
          confirmButtonClassName="btn btn-success"
          onConfirm={handleConfirmApprove}
          onCancel={() => setApproveConfirmId(null)}
        />
      )}

      {/* Bulk Action Confirmation */}
      {bulkAction !== null && (
        <ConfirmDeleteModal
          title={`Bulk ${bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)}`}
          message={
            bulkAction === 'approve' && selectedShadowbannedCount > 0
              ? `Approve ${selectedIds.size} submissions? ${selectedShadowbannedCount} of them ${selectedShadowbannedCount === 1 ? 'is' : 'are'} shadowbanned; approving will deliver those leads to their form's notification channels.`
              : `${bulkAction === 'delete' ? 'Delete' : bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)} ${selectedIds.size} submissions?`
          }
          confirmButtonClassName={bulkAction === 'approve' && selectedShadowbannedCount > 0 ? 'btn btn-success' : undefined}
          confirmButtonLabel={
            bulkAction === 'approve' && selectedShadowbannedCount > 0 ? '✓ Approve and Deliver' :
            bulkAction === 'approve' ? '✓ Approve All' :
            bulkAction === 'flag' ? '⚑ Flag All' :
            bulkAction === 'spam' ? '✕ Mark All as Spam' :
            'Delete All'
          }
          onConfirm={handleBulkAction}
          onCancel={() => setBulkAction(null)}
        />
      )}
    </div>
  );
};

export default SubmissionManager;
